/**
 * "Show me another way home", asked from another screen.
 *
 * The Options screen is opened from four places — the card on Alerts, the
 * card above the board, the button on a disruption, the button on a line —
 * and from a notification's own quick action, which reaches the app with no
 * screen behind it at all. None of those can carry an argument:
 * this app's router uses memory history, so `/tabs/options` is a path and
 * nothing else.
 *
 * So the question is left here and the screen picks it up as it enters, in the
 * same idiom as `src/board-request.js`. Taken, not read: a request is consumed
 * once, so coming back to the screen ten minutes later re-plans from wherever
 * the person is now rather than dragging them back to the station a
 * notification mentioned.
 *
 * Only two things are ever asked for — which station to leave from, and which
 * end of the commute is being aimed at. Everything else on that screen is read
 * live, because the answer is about the next hour.
 */
import { ref } from 'vue';

/** The two ends of a commute. Anything else is not a place this app knows. */
export const PLACE_KINDS = ['home', 'work'];

/**
 * When the morning stops being the morning.
 *
 * Noon, and no cleverer than that. Before it the question somebody is asking
 * is how to get to work; after it, how to get home. A rule's own window would
 * be a better answer on the days there is a rule, and a worse one on the days
 * there is not — this is the answer that is right on both, and the toggle is
 * one tap away when it is wrong.
 */
export const AFTERNOON_HOUR = 12;

const pending = ref(null);

/** Ask the options screen to plan this on its next entry. */
export function requestOptions({ fromStopId = null, toStopId = null, place = null } = {}) {
  const stopId = Number(fromStopId);
  const toId = Number(toStopId);
  pending.value = {
    fromStopId: Number.isFinite(stopId) && stopId > 0 ? stopId : null,
    // An EXPLICIT far end, which is what a shared link carries and what a place
    // kind must never be allowed to become. `place` stays for the four in-app
    // callers, where home and work are the person's own words on their own
    // phone; a link resolves them to a stop id before it leaves the device.
    // See the privacy note at the top of src/share.js.
    toStopId: Number.isFinite(toId) && toId > 0 ? toId : null,
    place: PLACE_KINDS.includes(place) ? place : null,
  };
  return pending.value;
}

/** The request, cleared. Null when nobody asked. */
export function takeRequestedOptions() {
  const asked = pending.value;
  pending.value = null;
  return asked;
}

/** Tests only. */
export function clearRequestedOptions() {
  pending.value = null;
}

/**
 * Which end of the commute the screen opens on.
 *
 * WHERE YOU ARE STANDING BEATS THE CLOCK, because it is a fact and the clock
 * is a habit. Somebody at Carnegie, which is their home station, is not asking
 * how to get home; somebody at the station by their office is not asking how
 * to get to work. The other end is the only question either of them can be
 * asking, at any hour, and reading it off the platform they are standing on is
 * both more often right and never a surprise: the answer agrees with what they
 * can see out of the window.
 *
 * The clock is the fallback for standing at neither of them, which is the
 * afternoon-or-morning guess this used to make everywhere. And the places
 * override both: somebody who has set only home is asking about home at nine
 * in the morning too, and offering them a WORK toggle for a station they never
 * named would be a control with nothing behind it. Null when neither place is
 * set, which is the empty state.
 */
/**
 * What the options screen is aiming at, from the request and the places.
 *
 * THREE ANSWERS, in the order of how much they are worth, and the first is
 * the one this function exists for:
 *
 *   1. AN EXPLICIT STOP ID, which only a shared link carries. It is a
 *      destination somebody was sent and it beats everything below, including
 *      both places and the clock. `kind` comes back NULL with it, because a
 *      link's destination is not an end of a commute and nothing downstream
 *      may treat it as one — that is the whole bug this answers: aimed at the
 *      recipient's own home instead, the screen fills with real trains to a
 *      real station and nothing anywhere says it is the wrong trip.
 *   2. A PLACE KIND the screen that opened this one named.
 *   3. The end of the commute worked out from where somebody is standing.
 *
 * `asked` marks the first two: somebody named this, and no guess may overrule
 * it. Pure, so the three cases can be checked without a screen.
 */
export function optionsTarget(asked, places, now = Date.now(), fromStopId = null) {
  const to = Number(asked?.toStopId);
  if (Number.isFinite(to) && to > 0) return { toStopId: to, kind: null, asked: true };
  if (PLACE_KINDS.includes(asked?.place) && places?.[asked.place]) {
    return { toStopId: Number(places[asked.place].stopId) || null, kind: asked.place, asked: true };
  }
  const kind = defaultPlaceKind(places, now, fromStopId);
  return { toStopId: Number(places?.[kind]?.stopId) || null, kind, asked: false };
}

export function defaultPlaceKind(places, now = Date.now(), fromStopId = null) {
  const has = (kind) => Boolean(places?.[kind]?.stopId);
  if (!has('home') && !has('work')) return null;
  if (!has('work')) return 'home';
  if (!has('home')) return 'work';
  const here = Number(fromStopId);
  if (Number.isFinite(here) && here > 0) {
    // Both are set by this point, so the far end always exists.
    if (Number(places.home.stopId) === here) return 'work';
    if (Number(places.work.stopId) === here) return 'home';
  }
  return new Date(now).getHours() < AFTERNOON_HOUR ? 'work' : 'home';
}
